import React, { useState, useEffect } from 'react';
import { createProduct, updateProduct, getCategories } from '../../services/api';
import toast from 'react-hot-toast';

const ProductForm = ({ editingItem, onClose, onRefresh }) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  const [formData, setFormData] = useState({
    name: editingItem?.name || '',
    category: editingItem?.category || '',
    price: editingItem?.price || '',
    stock: editingItem?.stock || 0,
    description: editingItem?.description || '',
    features: editingItem?.features?.join('\n') || '',
    imageUrl: editingItem?.imageUrl || '',
    // NEW FIELDS
    applicationDescription: editingItem?.applicationDescription || '',
    applicationImageUrl: editingItem?.applicationImageUrl || '',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== false
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [appImageFile, setAppImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(editingItem?.imageUrl || '');
  const [appImagePreview, setAppImagePreview] = useState(editingItem?.applicationImageUrl || '');
  const [loading, setLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'https://inteliheads.in/api';

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      let categoriesArray = [];
      
      if (response && response.data && Array.isArray(response.data)) {
        categoriesArray = response.data.filter(cat => cat.type === 'product');
      } else if (response && Array.isArray(response)) {
        categoriesArray = response.filter(cat => cat.type === 'product');
      } else if (response && response.categories && Array.isArray(response.categories)) {
        categoriesArray = response.categories.filter(cat => cat.type === 'product');
      }
      
      setCategories(categoriesArray);
      
      if (editingItem?.category && categoriesArray.length > 0 && !formData.category) {
        const existingCategory = categoriesArray.find(c => c.name === editingItem.category);
        if (existingCategory) {
          setFormData(prev => ({ ...prev, category: existingCategory.name }));
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([
        { _id: '1', name: 'Software', type: 'product' },
        { _id: '2', name: 'Hardware', type: 'product' },
        { _id: '3', name: 'Cloud Solutions', type: 'product' }
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const handleImageChange = (e, type = 'main') => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    if (type === 'main') {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setAppImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAppImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImageToS3 = async (file, currentUrl) => {
    if (!file) return currentUrl;
    
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/upload/s3/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Image uploaded successfully');
        return result.data.url;
      } else {
        toast.error(result.message || 'Upload failed');
        return currentUrl;
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading image');
      return currentUrl;
    } finally {
      setUploading(false);
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: newCategory.trim(),
          type: 'product',
          order: categories.length + 1,
          isActive: true
        })
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Category added successfully');
        setCategories([...categories, { _id: result.data._id, name: newCategory.trim(), type: 'product' }]);
        setFormData({ ...formData, category: newCategory.trim() });
        setNewCategory('');
        setShowNewCategoryInput(false);
      } else {
        toast.error(result.message || 'Failed to add category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Error adding category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    setLoading(true);
    
    try {
      let imageUrl = formData.imageUrl;
      let applicationImageUrl = formData.applicationImageUrl;
      
      if (imageFile) {
        imageUrl = await uploadImageToS3(imageFile, formData.imageUrl);
      }
      
      if (appImageFile) {
        applicationImageUrl = await uploadImageToS3(appImageFile, formData.applicationImageUrl);
      }
      
      const submitData = {
        name: formData.name,
        category: formData.category,
        price: formData.price,
        stock: parseInt(formData.stock) || 0,
        description: formData.description,
        features: formData.features ? formData.features.split('\n').filter(f => f.trim()) : [],
        imageUrl: imageUrl,
        // NEW FIELDS
        applicationDescription: formData.applicationDescription,
        applicationImageUrl: applicationImageUrl,
        order: parseInt(formData.order) || 0,
        isActive: formData.isActive
      };
      
      if (editingItem && editingItem._id) {
        await updateProduct(editingItem._id, submitData);
        toast.success('Product updated successfully');
      } else {
        await createProduct(submitData);
        toast.success('Product created successfully');
      }
      
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.message || 'Error saving product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000 
    }} onClick={onClose}>
      <div style={{ 
        background: 'white', 
        borderRadius: '16px', 
        padding: '2rem', 
        maxWidth: '700px', 
        width: '90%', 
        maxHeight: '85vh', 
        overflow: 'auto' 
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>
            {editingItem ? 'Edit Product' : 'Add New Product'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Product Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Product Name *</label>
            <input 
              type="text" 
              required 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Category Selection */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Category *</label>
            {loadingCategories ? (
              <div style={{ padding: '0.75rem', color: '#5A6E7F' }}>
                <i className="fas fa-spinner fa-spin"></i> Loading categories...
              </div>
            ) : (
              <>
                <select 
                  value={formData.category} 
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })} 
                  required
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginBottom: '0.5rem' }}
                >
                  <option value="">-- Select a category --</option>
                  {categories.map((cat) => (
                    <option key={cat._id || cat.name} value={cat.name}>
                      {cat.icon && <span style={{ marginRight: '0.5rem' }}>{cat.icon}</span>}
                      {cat.name}
                    </option>
                  ))}
                </select>
                
                {!showNewCategoryInput ? (
                  <button 
                    type="button"
                    onClick={() => setShowNewCategoryInput(true)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'transparent',
                      color: '#2C7A7B',
                      border: '1px solid #2C7A7B',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem'
                    }}
                  >
                    <i className="fas fa-plus"></i> Add New Category
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <input
                      type="text"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="Enter new category name"
                      style={{ flex: 1, padding: '0.5rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleAddNewCategory}
                      style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategory('');
                      }}
                      style={{ padding: '0.5rem 1rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Price and Stock */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Price</label>
              <input 
                type="text" 
                value={formData.price} 
                onChange={(e) => setFormData({ ...formData, price: e.target.value })} 
                placeholder="$999" 
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Stock Quantity</label>
              <input 
                type="number" 
                value={formData.stock} 
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })} 
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
            </div>
          </div>
          
          {/* Order and Active Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Display Order</label>
              <input 
                type="number" 
                value={formData.order} 
                onChange={(e) => setFormData({ ...formData, order: e.target.value })} 
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
              <small style={{ color: '#5A6E7F' }}>Lower numbers appear first</small>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={formData.isActive} 
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} 
                />
                <span>Active</span>
              </label>
            </div>
          </div>
          
          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Description</label>
            <textarea 
              value={formData.description} 
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
              rows="3" 
              placeholder="Product description"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Features */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Features (one per line)</label>
            <textarea 
              value={formData.features} 
              onChange={(e) => setFormData({ ...formData, features: e.target.value })} 
              rows="4" 
              placeholder="Feature 1&#10;Feature 2&#10;Feature 3" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>

          {/* ========== NEW SECTION: APPLICATION DETAILS ========== */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#F0F7FA',
            borderRadius: '12px',
            borderLeft: '4px solid #2C7A7B'
          }}>
            <h4 style={{ 
              marginBottom: '1rem', 
              color: '#0A192F',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <i className="fas fa-rocket" style={{ color: '#2C7A7B' }}></i>
              Application Details
            </h4>
            
            {/* Application Description */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Application Description</label>
              <textarea 
                value={formData.applicationDescription} 
                onChange={(e) => setFormData({ ...formData, applicationDescription: e.target.value })} 
                rows="3" 
                placeholder="Describe how this product is applied in real-world scenarios, use cases, industries served, etc."
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
            </div>
            
            {/* Application Image */}
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Application Image</label>
              
              {appImagePreview && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <img 
                    src={getFullImageUrl(appImagePreview)} 
                    alt="Application Preview" 
                    style={{ 
                      width: '100px', 
                      height: '100px', 
                      objectFit: 'cover', 
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0'
                    }} 
                  />
                </div>
              )}
              
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{
                  display: 'inline-block',
                  padding: '0.4rem 0.8rem',
                  background: '#2C7A7B',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}>
                  <i className="fas fa-upload"></i> Upload Application Image
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => handleImageChange(e, 'application')} 
                    style={{ display: 'none' }} 
                  />
                </label>
                <small style={{ color: '#5A6E7F', marginLeft: '0.5rem' }}>
                  Max 5MB, JPG, PNG, GIF, WEBP
                </small>
              </div>
              
              <input 
                type="text" 
                value={formData.applicationImageUrl} 
                onChange={(e) => {
                  setFormData({ ...formData, applicationImageUrl: e.target.value });
                  setAppImagePreview(e.target.value);
                  setAppImageFile(null);
                }} 
                placeholder="Or enter application image URL directly" 
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
              />
            </div>
          </div>
          
          {/* Product Main Image */}
          <div style={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Product Main Image</label>
            
            {imagePreview && (
              <div style={{ marginBottom: '0.5rem' }}>
                <img 
                  src={getFullImageUrl(imagePreview)} 
                  alt="Preview" 
                  style={{ 
                    width: '100px', 
                    height: '100px', 
                    objectFit: 'cover', 
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0'
                  }} 
                />
              </div>
            )}
            
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: '#2C7A7B',
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}>
                <i className="fas fa-upload"></i> Choose Image
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={(e) => handleImageChange(e, 'main')} 
                  style={{ display: 'none' }} 
                />
              </label>
              <small style={{ color: '#5A6E7F', marginLeft: '0.5rem' }}>
                Max 5MB, JPG, PNG, GIF, WEBP
              </small>
            </div>
            
            <input 
              type="text" 
              value={formData.imageUrl} 
              onChange={(e) => {
                setFormData({ ...formData, imageUrl: e.target.value });
                setImagePreview(e.target.value);
                setImageFile(null);
              }} 
              placeholder="Or enter image URL directly" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
          </div>
          
          {/* Submit Buttons */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button 
              type="submit" 
              disabled={loading || uploading} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#2C7A7B', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600,
                opacity: (loading || uploading) ? 0.7 : 1
              }}
            >
              {loading || uploading ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                <><i className="fas fa-save"></i> Save Product</>
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#5A6E7F', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontWeight: 600 
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;